import { type ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { GenericError } from "@via/components/error.tsx";
import { SkeletonLoader } from "@via/components/loading/loading.tsx";
import TableContextMenu from "@via/components/table/table-context-menu.tsx";
import Table from "@via/components/table/table.tsx";
import TagsCell from "@via/components/tags-cell.tsx";
import TagValueInput from "@via/components/tag-value-input.tsx";
import { usePipelineListByProject } from "@via/pages/projects/pipelines/pipelines.api.tsx";
import DeletePipelineModal from "@via/pages/projects/pipelines/workflow/components/delete-pipeline-modal.tsx";
import {
  getPipelineTypeIcon,
  getPipelineTypeLabel,
} from "@via/pages/projects/pipelines/workflow/utils/helpers.tsx";
import CreateRunModal from "@via/pages/projects/runs/create-run-modal.tsx";
import {
  EMPTY_TABLE_MESSAGE,
  ERROR,
  PIPELINE_TABLE_OPTIONS,
  ROUTES,
} from "@via/utils/constants.tsx";
import { formatDateString } from "@via/utils/helpers.tsx";
import {
  type PipelineInterface,
  type TableContextMenuInterface,
} from "@via/utils/interfaces.tsx";
import { TaggableEntityType } from "common";
import { type JSX, useCallback, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useProject } from "@via/pages/projects/projects.api.tsx";
import { useAbility } from "@via/context/ability-context";
import AiBadge from "@via/components/ai-badge";
import { useRegisterCortexContext } from "@via/hooks/useRegisterCortexContext";

export default function Pipelines(): JSX.Element {
  const navigate = useNavigate();
  const { projectId = "" } = useParams();
  const { data: projectData } = useProject(projectId);
  const [isDeletePipelineModalOpen, setIsDeletePipelineModalOpen] =
    useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10); // Default page size
  const [sort, setSort] = useState<Array<{ id: string; desc: boolean }>>([
    {
      id: "createdAt",
      desc: true,
    },
  ]);
  const [tagFilter, setTagFilter] = useState<string>("");
  const { data, isLoading, isError } = usePipelineListByProject(
    projectId,
    page,
    pageSize,
    sort,
    tagFilter ? [tagFilter] : []
  );
  const [selectedPipelineId, setSelectedPipelineId] = useState<string>();
  const [selectedPipelineName, setSelectedPipelineName] = useState<string>();
  const [isCreateRunModalOpen, setIsCreateRunModalOpen] = useState(false);
  const ability = useAbility([
    {
      action: "View",
      subject: "Button",
      condition: projectData?.own,
    },
  ]);
  // Check if the user has permission to delete pipeline
  const canDeletePipeline = ability.can("View", "Button");

  const columnHelper = createColumnHelper<PipelineInterface>();
  const handleTableAction = (
    option: TableContextMenuInterface,
    name: string,
    id: number
  ): void => {
    switch (option.name) {
      case "View Pipeline":
        navigate(`/pipelines/${id}`);
        break;
      case "Run":
        setSelectedPipelineId(`${id}`);
        setSelectedPipelineName(name);
        setIsCreateRunModalOpen(true);
        break;
      case "Remove":
        setSelectedPipelineId(`${id}`);
        setSelectedPipelineName(name);
        setIsDeletePipelineModalOpen(true);
        break;
      default:
        break;
    }
  };

  // Filter the options based on the ability to delete
  const filteredOptions = canDeletePipeline
    ? PIPELINE_TABLE_OPTIONS
    : PIPELINE_TABLE_OPTIONS.filter(option => option.name !== "Remove");

  const getProjectPipelinesContext = useCallback(() => {
    const pipelines = data?.data || [];

    return {
      project: {
        id: projectData?.id,
        name: projectData?.name,
        own: projectData?.own,
      },
      pipelines: pipelines.map(pipeline => ({
        id: pipeline.id,
        name: pipeline.name,
        summary: pipeline.summary,
        version: pipeline.version,
        pipelineType: pipeline.pipelineType,
        createdAt: pipeline.createdAt,
        modifiedAt: pipeline.modifiedAt,
      })),
      total: data?.total || 0,
      currentPage: page,
      pageSize: pageSize,
      sortBy: sort.length > 0 ? sort[0] : null,
    };
  }, [data?.data, data?.total, projectData, page, pageSize, sort]);

  useRegisterCortexContext({
    routePattern: ROUTES.PROJECT_PIPELINES,
    contextName: "Project Pipelines",
    getContext: getProjectPipelinesContext,
    systemPrompt:
      "You are analyzing project pipelines list page. Help answer questions about pipelines in this project, help find specific pipelines, and assist with filtering and sorting.",
    enabled: !!data && !!projectData,
  });

  const columns = [
    columnHelper.accessor("name", {
      cell: ({ row }) => {
        return (
          <>
            <Link
              to={`/pipelines/${row.original.id}`}
              className="via-link flex items-center gap-2">
              {row.original.name}{" "}
              <span className="via-badge">
                {getPipelineTypeIcon(row?.original?.pipelineType)}{" "}
                {getPipelineTypeLabel(row?.original?.pipelineType)}
              </span>
              {row.original.aiEntity ? <AiBadge /> : null}
            </Link>
            <p
              title={row.original.summary}
              className="text-xs">{`${row.original.summary}`}</p>
          </>
        );
      },
      header: () => <span>Name</span>,
    }),
    columnHelper.accessor("version", {
      cell: info => info.getValue(),
      header: () => <span>Version</span>,
    }),
    columnHelper.accessor("createdAt", {
      cell: info => {
        const value = info.getValue();
        return <span>{formatDateString(value, "LLL")}</span>;
      },
      header: () => <span>Date Created</span>,
    }),
    columnHelper.accessor("modifiedAt", {
      cell: info => {
        const value = info.getValue();
        return <span>{formatDateString(value, "LLL")}</span>;
      },
      header: () => <span>Last Modified</span>,
    }),
    columnHelper.display({
      id: "tags",
      cell: ({ row }) => (
        <TagsCell tags={row.original.tags ?? []} rowId={row.original.id} />
      ),
      header: () => <span>Tags</span>,
      enableSorting: false,
    }),
    columnHelper.accessor("id", {
      cell: ({ row }) => {
        return (
          <TableContextMenu
            options={filteredOptions}
            onSelect={option => {
              handleTableAction(option, row.original.name, row.original.id);
            }}
          />
        );
      },
      header: () => "Options",
    }),
  ] as Array<ColumnDef<PipelineInterface>>;

  return (
    <>
      {isLoading ? (
        <div className="grid grid-cols-1 gap-6 py-6 sm:grid-cols-2 lg:grid-cols-3">
          <SkeletonLoader count={12} />
        </div>
      ) : isError ? (
        <div className="via-generic-error">
          <GenericError message={ERROR} />
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <div className="w-64">
            <TagValueInput
              entityType={TaggableEntityType.Pipeline}
              value={tagFilter}
              onChange={value => {
                setTagFilter(value);
                setPage(0);
              }}
            />
          </div>
          <Table
            data={data?.data}
            columns={columns}
            sort={sort}
            setSort={setSort}
            page={page}
            setPage={setPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
            total={data?.total}
            enableSorting={false}
            emptyMessage={EMPTY_TABLE_MESSAGE.PIPELINES}
          />
        </div>
      )}
      {isDeletePipelineModalOpen && (
        <DeletePipelineModal
          open={isDeletePipelineModalOpen}
          setOpen={setIsDeletePipelineModalOpen}
          pipelineName={selectedPipelineName}
          pipelineId={selectedPipelineId}
        />
      )}
      {isCreateRunModalOpen && (
        <CreateRunModal
          open={isCreateRunModalOpen}
          setOpen={setIsCreateRunModalOpen}
          projectId={projectId}
          pipelineId={selectedPipelineId}
        />
      )}
    </>
  );
}
